<script setup lang="ts">
/**
 * Bottom-of-pane progress bar for the matter-context modal. Three
 * visible states (hidden in idle):
 *
 *   - analyzing: centered text-only row — assistant-star icon + phase
 *     label + animated ellipsis (no chrome, no progress track, no ETA)
 *   - complete (not dismissed): assistant icon + "Analysis complete",
 *     count summary, manual Dismiss button. A red top border grows
 *     from 0% to 100% over AUTO_DISMISS_MS, after which the bar
 *     auto-dismisses.
 *   - error: terminal error glyph (currently same chrome as analyzing
 *     but with no fill); error message is shown by the left pane.
 *
 * Once Dismissed (manually or by timeout), the parent re-shows the
 * (disabled) Extract button so the user knows extraction has already
 * run for this modal open.
 *
 * Phase + ETA are mocked client-side — see
 * docs_for_claude/matter_context/matter-context-frontend-design.md
 * (deferred items §Backend) for the integration this is waiting on.
 */
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMatterContextModalStore } from '../../stores/matterContextModalStore';
import assistantStarIcon from '../../assets/assistant-star.svg';
import assistantIcon from '../../assets/assistant.svg';

const AUTO_DISMISS_MS = 8000;

const { t } = useI18n();
const store = useMatterContextModalStore();

const visible = computed(() => store.extractionState !== 'idle' && !store.progressDismissed);

const isComplete = computed(() => store.extractionState === 'complete');

const completionSummary = computed(() => {
    const e = store.events.length;
    const n = store.entities.length;
    const eventsPart = t('matterContext.progress.eventsCount', e);
    const entitiesPart = t('matterContext.progress.entitiesCount', n);
    return t('matterContext.progress.completionSummary', {
        events: eventsPart,
        entities: entitiesPart,
    });
});

// Auto-dismiss: when complete, flip `timeoutActive` after a frame so
// the CSS transition runs from 0% to 100% width, then dismiss when the
// timer fires. Manual Dismiss / state change cancels both.
const timeoutActive = ref(false);
let dismissTimer: ReturnType<typeof setTimeout> | null = null;

const stopTimer = () => {
    if (dismissTimer !== null) {
        clearTimeout(dismissTimer);
        dismissTimer = null;
    }
    timeoutActive.value = false;
};

watch(
    [() => store.extractionState, () => store.progressDismissed],
    ([state, dismissed]) => {
        stopTimer();
        if (state === 'complete' && !dismissed) {
            // Frame 1: render bar at 0%. Frame 2: flip to active so CSS
            // transitions the width to 100% over AUTO_DISMISS_MS.
            void nextTick(() => {
                timeoutActive.value = true;
            });
            dismissTimer = setTimeout(() => {
                store.dismissProgress();
            }, AUTO_DISMISS_MS);
        }
    },
    { immediate: true },
);

onUnmounted(stopTimer);

const timeoutBarStyle = computed(() => ({
    transitionDuration: `${AUTO_DISMISS_MS}ms`,
}));
</script>

<template>
    <div v-if="visible" class="mc-progress" :class="`mc-progress-${store.extractionState}`">
        <div
            v-if="isComplete"
            class="mc-progress-timeout-bar"
            :class="{ 'mc-progress-timeout-bar-active': timeoutActive }"
            :style="timeoutBarStyle"
        ></div>
        <div class="mc-progress-row">
            <img
                :src="isComplete ? assistantIcon : assistantStarIcon"
                alt=""
                class="mc-progress-icon"
                :class="{ 'mc-progress-icon-shimmer': store.extractionState === 'analyzing' }"
            />
            <span class="mc-progress-label">{{ store.phaseLabel }}</span>
            <span
                v-if="store.extractionState === 'analyzing'"
                class="mc-progress-ellipsis"
                aria-hidden="true"
            >
                <span v-for="i in 6" :key="i" class="mc-progress-ellipsis-dot">.</span>
            </span>
            <button
                v-if="isComplete"
                type="button"
                class="mc-progress-dismiss"
                @click="store.dismissProgress()"
            >
                {{ t('matterContext.progress.dismiss') }}
            </button>
        </div>
        <p v-if="isComplete" class="mc-progress-summary">{{ completionSummary }}</p>
    </div>
</template>
